import { Phone, Search, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { EmployeePage } from '@/components/employee/EmployeePage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { useEmployeeClients } from '@/hooks/employee-portal';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * /employee/clients — ROADMAP.md §6.7, open question **Q5**.
 *
 * **Every client is here, which is Q5's default and matches the prototype.** The
 * crew may rely on being able to look a customer up, and removing that is a
 * change the business did not ask for.
 *
 * **No addresses.** business-rules.md §2 gives an employee properties "read,
 * assigned only", so a cleaner sees an address through a job they were sent to —
 * where it is already theirs — rather than by browsing. Access notes, the
 * lockbox and gate codes, appear on exactly one screen in this portal and only
 * for a job they are on.
 */

export function ClientsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const clients = useEmployeeClients({ page, search });

  return (
    <EmployeePage
      title="Clients"
      description="Look up a customer, see the jobs you have done for them, and leave a note."
    >
      <div className="mb-4">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search clients"
          placeholder="Name, phone or email"
          className="sm:w-80"
        />
      </div>

      {/*
        Three branches, and they are different things. **This is the screen P22
        was found on** — the prototype renders a failed request as "No jobs found
        for this client" — so an error here says so and offers to retry.
      */}
      {clients.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load the client list"
            hint="Something went wrong on our side — this does not mean there are no clients. Try again, and ring the office if it keeps happening."
            onRetry={() => {
              void clients.refetch();
            }}
          />
        </div>
      ) : clients.isPending ? (
        <ul aria-busy="true" className="space-y-3">
          <span className="sr-only">Loading clients</span>
          {[0, 1, 2].map((key) => (
            <li key={key} aria-hidden="true" className="h-24 animate-pulse rounded-lg bg-ink-100" />
          ))}
        </ul>
      ) : clients.data && clients.data.data.length > 0 ? (
        <ul className="space-y-3">
          {clients.data.data.map((entry) => (
            <li key={entry.id}>
              <Link
                to={`/employee/clients/${entry.id}`}
                className="block rounded-lg border border-ink-300 bg-white p-4 shadow-card hover:border-honey-500"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-h3 text-ink-900">{entry.displayName}</p>
                    {entry.displayName === entry.contactName ? null : (
                      <p className="mt-0.5 text-meta text-ink-500">{entry.contactName}</p>
                    )}
                  </div>
                  <span className="shrink-0 rounded-full bg-ink-100 px-2.5 py-0.5 text-meta text-ink-700">
                    {/* Zero is `0`, and it means "you have not been here" — a
                        fact worth showing rather than hiding (P1). */}
                    {entry.myJobCount} job{entry.myJobCount === 1 ? '' : 's'} for you
                  </span>
                </div>

                {entry.phone === null ? null : (
                  <p className="mt-2 flex items-center gap-2 text-body text-ink-700">
                    <Phone aria-hidden="true" className="size-4 shrink-0 text-ink-500" />
                    {entry.phone}
                  </p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={search === '' ? Users : Search}
            title={search === '' ? 'No clients yet.' : 'Nothing matches that search.'}
            hint={
              search === ''
                ? 'Customers the office takes on appear here.'
                : 'Try part of a name, a phone number, or an email address.'
            }
          />
        </div>
      )}

      {clients.data ? (
        <Pagination
          page={clients.data.page}
          pageSize={clients.data.pageSize || DEFAULT_PAGE_SIZE}
          total={clients.data.total}
          onPageChange={setPage}
          itemNoun="clients"
        />
      ) : null}
    </EmployeePage>
  );
}
